import { Box, useTheme } from "@mui/material";
import { NavProvider, Team as TWTeam } from "@teamworksdev/nav";
import { Suspense, lazy, useEffect } from "react";
import { Navigate, Route, Routes, useNavigate } from "react-router-dom-v5-compat";
import { Loading } from "shared-ui/global/Loading";
import { checkForSchemaVersionUpdate } from "shared-ui/utils/schemaVersion";
import { UserOrgMembership } from "shared-ui/utils/tokens";
import { useCookieContext } from "../../contexts/Cookie";
import { environment } from "../../environment";
import { useNutritionFlags } from "../../hooks/useNutritionFlags";
import { clearReportingUser } from "../../reporting/reporting";
import { useApiCredentialsAndSchemaQuery, useCanBeStaffQuery, useMyAxleDataForNavbarQuery } from "../../types";
import { setLocalStorageValue } from "../../utils/localStorage";
import { REFRESH_TOKEN_PAYLOAD_AND_HEADER_COOKIE, refresh, useUser } from "../../utils/tokens";
import { ActivateUserPage } from "../NoAuth/ActivateUserPage";
import { NoAuthNavLayout } from "../NoAuth/NoAuthNavLayout";
import { NAV_API_DOCS } from "../NoAuth/NoAuthPaths";
import { AppUpdateCheckPage } from "./AppUpdateCheckPage";
import { NAV_MOBILE_WELCOME, NAV_ROOT, NAV_SWITCH_ORG, NAV_UPDATE_CHECK } from "./AuthPaths";
import { MobileWelcomePage } from "./MobileWelcomePage";
import { useMemberships, useSwitchOrgs } from "./Org/OrgMembershipUtils";
import { OrgApp } from "./OrgApp";
import { getFallbackUri, getOrgUserDetails } from "./OrgUtils";
import { Link, STAFF_ORG_ID, staffOrg } from "./PrimaryNavUtils";
import { StaffRouter } from "./Staff/StaffRouter";
import { SwitchOrgPage } from "./SwitchOrgPage";
import { useTeamworksAuthContext } from "../../contexts/TeamworksAuth";

const ApiDocsPage = lazy(() => import("../NoAuth/ApiDocsPage").then(module => ({ default: module.ApiDocsPage })));

export const AuthRouter = () => {
  const navigate = useNavigate();
  const user = useUser();
  const orgUserDetails = getOrgUserDetails(user);
  const { isStaff } = orgUserDetails;
  const memberships = useMemberships();
  const { getCookieValue } = useCookieContext();
  const { data: canBeStaffData } = useCanBeStaffQuery();
  const { data: schemaData, loading: schemaLoading } = useApiCredentialsAndSchemaQuery({ fetchPolicy: "network-only" });
  const { isTeamworksUserManagementEnabled, isAppSwitcherEnabled } = useNutritionFlags();

  const canBeStaff = !!canBeStaffData?.canBeStaff;
  const isOrCanBeStaff = isStaff || canBeStaff;
  const canSwitchOrgs = !!(memberships && ((isOrCanBeStaff && memberships.length) || memberships.length > 1));
  const orgFromStaff = isOrCanBeStaff ? [staffOrg] : [];
  const orgsFromMemberships = memberships?.map(({ org: { id, name } }: UserOrgMembership): TWTeam => ({ id, name })) || [];
  const orgs = canSwitchOrgs ? [...orgFromStaff, ...orgsFromMemberships] : [];
  const hasAppSwitcherAllowedFF = isAppSwitcherEnabled || isTeamworksUserManagementEnabled;
  const { data: axleData, loading: axleDataLoading } = useMyAxleDataForNavbarQuery({
    fetchPolicy: "cache-and-network",
    skip: isStaff || !hasAppSwitcherAllowedFF,
  });
  const {
    palette: {
      products: { performance },
    },
  } = useTheme();
  const hasRefreshToken = !!getCookieValue(REFRESH_TOKEN_PAYLOAD_AND_HEADER_COOKIE);
  const fallbackUri = getFallbackUri(hasRefreshToken, orgUserDetails);

  // Teamworks Auth for MFEs
  const { handleTwAuthClientState, shouldAuthTwClient, isAuthReady } = useTeamworksAuthContext();
  useEffect(() => {
    handleTwAuthClientState();
  }, []);

  useEffect(() => {
    if (schemaData && !schemaLoading) {
      const update = checkForSchemaVersionUpdate({
        serverSchemaVersion: schemaData.webVersion,
        clientSchemaVersion: environment.VITE_WEB_SCHEMA_VERSION,
      });
      if (update === "majorUpdate" || update === "minorUpdate") {
        navigate(NAV_UPDATE_CHECK);
      }
      setLocalStorageValue("nixAppId", schemaData.nixAppId);
      setLocalStorageValue("nixAppKey", schemaData.nixAppKey);
    }
  }, [schemaData, schemaLoading, navigate]);

  const switchOrgs = useSwitchOrgs();
  const handleOrgChange = async (orgId: string) => {
    clearReportingUser();
    if (orgId === STAFF_ORG_ID) {
      await refresh();
    } else {
      await switchOrgs(orgId);
    }
    navigate(NAV_ROOT);
  };
  // only show app switcher if the feature flag is enabled
  const axleDataForNavbar = hasAppSwitcherAllowedFF ? axleData?.myAxleDataForNavbar : undefined;

  const userOrgColors = axleDataForNavbar ? {
    top: axleDataForNavbar.navbarTopColor ?? "#000000",
    bottom: axleDataForNavbar.navbarBottomColor ?? performance.primary,
  } : {
    top: "#000000",
    bottom: performance.primary,
  };

  if (axleDataLoading || (shouldAuthTwClient && !isAuthReady)) {
    return (
      <Box sx={{ height: "100vh", width: "100vw" }}>
        <Loading />
      </Box>
    );
  }
  return (
    <NavProvider colors={userOrgColors} Link={Link}>
      <Routes>
        <Route
          path="/org/*"
          element={<OrgApp orgs={orgs} onOrgChange={handleOrgChange} axleDataForNavbar={axleDataForNavbar} />}
        />
        <Route path="/staff/*" element={<StaffRouter orgs={orgs} onOrgChange={handleOrgChange} />} />
        <Route path={NAV_UPDATE_CHECK} element={<AppUpdateCheckPage />} />
        <Route path={NAV_SWITCH_ORG} element={<SwitchOrgPage />} />
        <Route element={<NoAuthNavLayout />}>
          <Route path={NAV_MOBILE_WELCOME} element={<MobileWelcomePage />} />
          <Route path="/activate/:activateToken" element={<ActivateUserPage />} />
        </Route>
        <Route
          path={NAV_API_DOCS}
          element={
            <Suspense fallback={<Loading />}>
              <ApiDocsPage />
            </Suspense>
          }
        />
        <Route path="*" element={<Navigate to={fallbackUri} replace />} />
      </Routes>
    </NavProvider>
  );
};
